import React, { useState, useEffect } from 'react';
import { Spinner, DropZone as PolarisDropzone, Button, Modal, Text } from '@shopify/polaris';
import { isMobile } from 'react-device-detect';
import Dropzone from 'react-dropzone';
import getSharePointUrlForFirnrDocumentTypeIdDocName, { getSharePointPath } from '../../../utils/sharepoint';
import { convertDocumentTZ, getRequest } from '../functions';
import { useLookupStore } from '../../../context/useStore';
import StepModelPreview from './StepModelPreview';
import FileListBrowser from './FileListBrowser';
import { formatFileDate, getBadgeColor, getFileExtension, imagePreviewExtensions, textPreviewExtensions } from './fileTypes';

const getPreviewType = (fileName) => {
  const extension = getFileExtension(fileName);

  if (extension === 'pdf') return 'pdf';
  if (['stp', 'step'].includes(extension)) return 'step';
  if (imagePreviewExtensions.includes(extension)) return 'image';
  if (textPreviewExtensions.includes(extension)) return 'text';

  return 'unsupported';
};

const revokePreviewUrl = (previewData) => {
  if (previewData?.type === 'blob' && previewData.url) {
    URL.revokeObjectURL(previewData.url);
  }
};

export default function DetailFileBrowser({
  FIRNR,
  Klantnummer,
  resourceId,
  docTypeId,
  docNumber,
  docType,
  heightPxs,
  parentRefetchDocuments = console.log,
  showPreviewModalButton = false,
  previewModalTitle = 'Document preview'
}) {
  const documenttypes = useLookupStore((state) => state.documenttypes);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewModalActive, setPreviewModalActive] = useState(false);
  const [selectedPreviewFileId, setSelectedPreviewFileId] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [showReferenceFigure, setShowReferenceFigure] = useState(false);

  const selectedPreviewFile = docs.find((file) => file.id === selectedPreviewFileId) || null;
  const selectedFileIsStep = ['stp', 'step'].includes(getFileExtension(selectedPreviewFile?.name));

  useEffect(() => {
    return () => revokePreviewUrl(previewData);
  }, [previewData]);

  useEffect(() => {
    if (!previewModalActive || docs.length === 0) return;
    if (!selectedPreviewFileId || !docs.some((file) => file.id === selectedPreviewFileId)) {
      setSelectedPreviewFileId(docs[0].id);
    }
  }, [docs, previewModalActive, selectedPreviewFileId]);

  useEffect(() => {
    if (!selectedFileIsStep) {
      setShowReferenceFigure(false);
    }
  }, [selectedFileIsStep]);

  useEffect(() => {
    if (!previewModalActive || !selectedPreviewFile || !documenttypes) return;

    let isCancelled = false;

    const loadPreview = async () => {
      setPreviewLoading(true);
      setPreviewError(null);
      setPreviewData((currentPreviewData) => {
        revokePreviewUrl(currentPreviewData);
        return null;
      });

      try {
        const sharepointPath = getSharePointPath(Klantnummer, selectedPreviewFile.docTypeId, selectedPreviewFile.docNumber || docNumber, documenttypes);
        const response = await fetch('/api/docgen/streamsharepointdoc', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ path: sharepointPath, fileName: selectedPreviewFile.name })
        });

        if (!response.ok) throw new Error('Preview download failed');

        const blob = await response.blob();
        const previewType = getPreviewType(selectedPreviewFile.name);

        let nextPreviewData = { type: 'unsupported' };
        if (previewType === 'text') {
          nextPreviewData = { type: 'text', content: await blob.text() };
        } else if (previewType === 'step') {
          nextPreviewData = { type: 'step', arrayBuffer: await blob.arrayBuffer(), sharepointPath };
        } else if (previewType === 'pdf' || previewType === 'image') {
          nextPreviewData = { type: 'blob', url: URL.createObjectURL(blob), fileType: previewType };
        }

        if (isCancelled) {
          revokePreviewUrl(nextPreviewData);
          return;
        }

        setPreviewData(nextPreviewData);
      } catch (error) {
        if (!isCancelled) {
          console.error('Error loading SharePoint preview:', error);
          setPreviewError('Preview could not be loaded for this file.');
        }
      } finally {
        if (!isCancelled) setPreviewLoading(false);
      }
    };

    loadPreview();

    return () => {
      isCancelled = true;
    };
  }, [Klantnummer, docNumber, documenttypes, previewModalActive, selectedPreviewFile]);

  async function fetchDocuments() {
    if (resourceId !== 'new') {
      let data = [];

      if (docTypeId !== 28 && docTypeId !== 22) {
        data = await getRequest(`/api/documents/id/typeid?id=${resourceId}&typeID=${docTypeId}`);
      } else {
        data = await getRequest(`/api/documents/id/typeid?firnr=${FIRNR}&typeID=${docTypeId}`);
      }

      if (Array.isArray(data)) {
        const files = data.map((doc) => ({
          id: String(doc.DocumentID),
          name: doc.Filename,
          isDir: false,
          modDate: convertDocumentTZ(doc.Datum),
          docTypeId: doc.DocumentTypeID,
          docNumber: doc.docNumber
        }));
        setDocs(files);
      }
    }
  }

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceId, FIRNR]);

  function handleOpenFile(file) {
    const { docTypeId: selectedDocTypeId, docNumber: selectedDocNumber, name } = file;
    const url = getSharePointUrlForFirnrDocumentTypeIdDocName(Klantnummer, selectedDocTypeId, selectedDocNumber || docNumber, name, documenttypes);
    window.open(url, '_newtab');
  }

  async function handleDeleteFiles(selectedFiles) {
    setLoading(true);

    for (let i = 0; i < selectedFiles.length; i++) {
      const { id, name } = selectedFiles[i];
      const payload = {
        id: id,
        name: name,
        customerNumber: Klantnummer,
        docType: docType,
        docNumber: docNumber
      };

      await fetch(`/api/docgen`, { method: 'DELETE', headers: { 'Content-type': 'application/json; charset=UTF-8' }, body: JSON.stringify(payload) });
    }

    await fetchDocuments();
    await parentRefetchDocuments();
    setSelectedPreviewFileId((currentPreviewFileId) => (selectedFiles.some((file) => file.id === currentPreviewFileId) ? null : currentPreviewFileId));
    setLoading(false);
  }

  const handleDropZoneDrop = async (files) => {
    if (files.some((file) => file.name.includes('#'))) return alert('File name(s) cannot contain a #');

    setLoading(true);

    for (let i = 0; i < files.length; i++) {
      var formData = new FormData();
      formData.append('file', files[i]);
      formData.append('customerNumber', Klantnummer);
      formData.append('docType', docType);
      formData.append('docNumber', docNumber);
      formData.append('FIRNR', FIRNR);
      formData.append('resourceId', resourceId);
      formData.append('docTypeId', docTypeId);
      formData.append('isSystemDoc', '0');

      await fetch(`/api/docgen/uploadfile`, { method: 'POST', body: formData });
    }

    await fetchDocuments();
    await parentRefetchDocuments();
    setLoading(false);
  };

  const fileUpload = (
    <div style={{ padding: '1rem' }}>
      <Button>ADD PICS OR FILES</Button>
    </div>
  );

  const handleClosePreviewModal = () => {
    setPreviewModalActive(false);
    setPreviewError(null);
    setPreviewData((currentPreviewData) => {
      revokePreviewUrl(currentPreviewData);
      return null;
    });
  };

  const getSelectedPreviewFileUrl = () => {
    if (!selectedPreviewFile) return null;

    return getSharePointUrlForFirnrDocumentTypeIdDocName(Klantnummer, selectedPreviewFile.docTypeId, selectedPreviewFile.docNumber || docNumber, selectedPreviewFile.name, documenttypes);
  };

  const renderPreviewPane = () => {
    if (previewLoading) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Spinner accessibilityLabel="Loading preview" size="large" />
        </div>
      );
    }

    if (!selectedPreviewFile) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Text tone="subdued">Select a file to preview it.</Text>
        </div>
      );
    }

    if (previewError) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', textAlign: 'center' }}>
          <Text tone="critical">{previewError}</Text>
        </div>
      );
    }

    if (previewData?.type === 'text') {
      return (
        <div style={{ height: '100%', overflow: 'auto', background: '#f6f6f7', borderRadius: '8px', padding: '12px' }}>
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace', fontSize: '12px' }}>{previewData.content}</pre>
        </div>
      );
    }

    if (previewData?.type === 'blob' && previewData.fileType === 'pdf') {
      return <iframe title={`Preview of ${selectedPreviewFile.name}`} src={previewData.url} style={{ width: '100%', height: '100%', border: 'none', borderRadius: '8px' }} />;
    }

    if (previewData?.type === 'blob' && previewData.fileType === 'image') {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: '#f6f6f7', borderRadius: '8px', padding: '16px' }}>
          <img src={previewData.url} alt={selectedPreviewFile.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
        </div>
      );
    }

    if (previewData?.type === 'step') {
      return <StepModelPreview arrayBuffer={previewData.arrayBuffer} fileName={selectedPreviewFile.name} sharepointPath={previewData.sharepointPath} showReferenceFigure={showReferenceFigure} />;
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', height: '100%', textAlign: 'center' }}>
        <Text tone="subdued">Preview is not available for this file type yet.</Text>
        <Button onClick={() => window.open(getSelectedPreviewFileUrl(), '_blank')}>Open in SharePoint</Button>
      </div>
    );
  };

  return (
    <div>
      {loading ? (
        <div style={{ marginTop: '20px', fontWeight: '500', marginBottom: '15px' }}>
          <span style={{ paddingRight: '5px' }}>
            <Spinner accessibilityLabel="Spinner example" size="small" />
          </span>
          SYNCING WITH SHAREPOINT
          <span style={{ paddingLeft: '5px' }}>
            <Spinner accessibilityLabel="Spinner example" size="small" />
          </span>
        </div>
      ) : null}

      <div style={{ marginBottom: '15px' }}>
        {showPreviewModalButton && docs.length > 0 ? (
          <div style={{ display: 'flex', justifyContent: 'flex-end', paddingBottom: '10px' }}>
            <Button
              onClick={() => {
                setPreviewModalActive(true);
                if (!selectedPreviewFileId && docs[0]) setSelectedPreviewFileId(docs[0].id);
              }}
            >
              Open large preview
            </Button>
          </div>
        ) : null}

        <div style={{ paddingBottom: '10px', display: isMobile ? 'inline' : 'none' }}>
          <PolarisDropzone onDrop={handleDropZoneDrop} variableHeight>
            {fileUpload}
          </PolarisDropzone>
        </div>

        <Dropzone onDrop={handleDropZoneDrop} disabled={loading} noClick noKeyboard>
          {({ getRootProps, isDragActive }) => (
            <div {...getRootProps()} data-file-list-container style={{ resize: 'vertical', overflow: 'hidden', height: heightPxs || '250px', minHeight: '140px' }}>
              <FileListBrowser
                files={docs}
                onOpenFile={handleOpenFile}
                onDeleteFiles={handleDeleteFiles}
                disabled={loading}
                isDragActive={isDragActive}
                emptyMessage={isMobile ? 'No files yet.' : 'No files yet. Drag files here to upload them.'}
              />
            </div>
          )}
        </Dropzone>
      </div>

      <Modal open={previewModalActive} onClose={handleClosePreviewModal} title={previewModalTitle} size="large">
        <Modal.Section>
          <div style={{ display: 'grid', gridTemplateColumns: '340px minmax(0, 1fr)', gap: '20px', height: '80vh' }}>
            <div style={{ borderRight: '1px solid #d2d5d8', paddingRight: '16px', overflowY: 'auto' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {docs.map((file) => {
                  const isSelected = file.id === selectedPreviewFileId;

                  return (
                    <button
                      key={file.id}
                      type="button"
                      onClick={() => setSelectedPreviewFileId(file.id)}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        border: isSelected ? '1px solid #1f6feb' : '1px solid #d2d5d8',
                        borderRadius: '8px',
                        padding: '12px',
                        background: isSelected ? '#eff6ff' : '#ffffff',
                        cursor: 'pointer'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                        <div style={{ fontWeight: 600, wordBreak: 'break-word' }}>{file.name}</div>
                        <div
                          style={{
                            flexShrink: 0,
                            padding: '2px 8px',
                            borderRadius: '999px',
                            background: getBadgeColor(file.name),
                            color: '#ffffff',
                            fontSize: '11px',
                            fontWeight: 600,
                            textTransform: 'uppercase'
                          }}
                        >
                          {getFileExtension(file.name)}
                        </div>
                      </div>
                      <div style={{ paddingTop: '6px', color: '#6d7175', fontSize: '12px' }}>{formatFileDate(file.modDate) || 'Unknown date'}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, wordBreak: 'break-word' }}>{selectedPreviewFile?.name || 'No file selected'}</div>
                  {selectedFileIsStep ? <div style={{ color: '#6d7175', fontSize: '12px', paddingTop: '2px' }}>Drag to orbit, scroll to zoom, and right-drag to pan the STEP model.</div> : null}
                </div>
                {selectedPreviewFile ? (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {selectedFileIsStep ? (
                      <Button pressed={showReferenceFigure} onClick={() => setShowReferenceFigure((currentValue) => !currentValue)}>
                        {showReferenceFigure ? 'Hide size reference' : 'Show 180 cm person'}
                      </Button>
                    ) : null}
                    <Button onClick={() => window.open(getSelectedPreviewFileUrl(), '_blank')}>Open in SharePoint</Button>
                  </div>
                ) : null}
              </div>
              <div style={{ flex: 1, minHeight: 0 }}>{renderPreviewPane()}</div>
            </div>
          </div>
        </Modal.Section>
      </Modal>
      {previewModalActive ? (
        <style>{`
          .Polaris-Modal-Dialog__Modal {
            width: min(96vw, 1800px) !important;
            max-width: 96vw !important;
          }
        `}</style>
      ) : null}
    </div>
  );
}
